import { useCashierAuthStore } from '../../store/cashierAuthStore';
import type { AuthUser, AuthSubsidiary, AuthBranch } from '../../lib/cashierAuthClient';
import { useLang } from '../../lib/language';
import { PickerCard, PickerLayout, EmptyState } from './PickerLayout';

export function BranchPickerScreen({
  user, subsidiary, branches, busy,
}: {
  user: AuthUser;
  subsidiary: AuthSubsidiary;
  branches: AuthBranch[];
  busy: boolean;
}): JSX.Element {
  const { t } = useLang();
  const error = useCashierAuthStore((s) => s.error);
  const choose = useCashierAuthStore((s) => s.chooseBranch);
  const back = useCashierAuthStore((s) => s.backToSubsidiaryPicker);
  const logout = useCashierAuthStore((s) => s.logout);

  // A branch whose drawer is switched off is greyed out like one with no
  // drawer — and without this banner the login simply stopped there, with
  // nothing on screen to say why. Shown when that is the ONLY reason no
  // branch can be picked; a server error takes precedence.
  const inactiveMsg = t({
    en: 'The cash drawer for this branch is inactive. Contact your administrator.',
    ar: 'درج النقدية لهذا الفرع غير نشط. تواصل مع المسؤول.',
  });
  const nothingSelectable = branches.length > 0 && branches.every((b) => !b.has_cash_drawer);
  const banner =
    error ?? (nothingSelectable && branches.some((b) => b.drawer_inactive) ? inactiveMsg : null);

  if (branches.length === 0) {
    return (
      <EmptyState
        title={t({ en: 'No branches in this company', ar: 'لا توجد فروع في هذه الشركة' })}
        message={t({ en: `You don't have access to any branches in ${subsidiary.name}.`, ar: `ليس لديك صلاحية الوصول إلى أي فرع في ${subsidiary.name}.` })}
        userLabel={t({ en: `Signed in as ${user.username}`, ar: `مُسجّل الدخول باسم ${user.username}` })}
        primaryLabel={t({ en: 'Choose another company', ar: 'اختر شركة أخرى' })}
        onPrimary={back}
        secondaryLabel={t({ en: 'Sign out', ar: 'تسجيل الخروج' })}
        onSecondary={() => logout()}
        busy={busy}
      />
    );
  }

  return (
    <PickerLayout
      title={t({ en: 'Choose a branch', ar: 'اختر فرعًا' })}
      subtitle={`${subsidiary.name} · ${user.username}`}
      error={banner}
      onBack={back}
      backLabel={t({ en: '← Choose company', ar: '← اختر شركة' })}
      onLogout={() => logout()}
      busy={busy}
    >
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {branches.map((b) => (
          <PickerCard
            key={b.id}
            title={b.name}
            subtitle={[
              b.code ? t({ en: `Code: ${b.code}`, ar: `الرمز: ${b.code}` }) : null,
              !b.has_cash_drawer
                ? b.drawer_inactive
                  ? t({ en: '⚠ Cash drawer inactive (cannot select)', ar: '⚠ درج النقدية غير نشط (لا يمكن الاختيار)' })
                  : t({ en: '⚠ No cash drawer (cannot select)', ar: '⚠ لا يوجد درج نقدية (لا يمكن الاختيار)' })
                : null,
            ].filter(Boolean).join(' · ')}
            disabled={busy || !b.has_cash_drawer}
            onClick={() => void choose(b.id)}
          />
        ))}
      </ul>
    </PickerLayout>
  );
}
